import type { NotificationPreferences } from '../../notifications/notification-category';

interface NotificationCacheMember {
	id?: string;
	businessUserId?: string;
	businessRole?: string;
	notificationSettings?: NotificationPreferences | null;
}

interface NotificationCacheBusiness {
	id: string;
	role?: string;
	users?: NotificationCacheMember[];
	businessUsers?: NotificationCacheMember[];
}

export interface BusinessUserUpdateCacheResponse {
	id?: string;
	businessUserId?: string;
	updatedBusinessUserId?: string;
	businessRole?: string;
	isApprover?: boolean;
	canPurchase?: boolean;
	adEditor?: boolean;
	[key: string]: unknown;
}

interface NotificationCacheUser extends NotificationCacheMember {
	businessRole?: string;
}

interface NotificationCacheBusinessUser extends NotificationCacheMember {
	role?: string;
	isApprover?: boolean;
	canPurchase?: boolean;
	adEditor?: boolean;
}

export function preserveHydratedBusinessUsers<T extends NotificationCacheBusiness>(
	businesses: readonly T[],
	cachedBusinesses: Readonly<Record<string, T | undefined>>
): T[] {
	return businesses.map(business => {
		const cachedBusiness = cachedBusinesses[business.id];
		const preservedRole = business.role ?? cachedBusiness?.role;
		const businessWithPreservedRole = preservedRole === business.role ? business : { ...business, role: preservedRole };
		const hasHydratedNotificationSettings = [...(cachedBusiness?.users || []), ...(cachedBusiness?.businessUsers || [])].some(user =>
			Object.prototype.hasOwnProperty.call(user, 'notificationSettings')
		);

		if (!cachedBusiness || !hasHydratedNotificationSettings) {
			return businessWithPreservedRole;
		}

		return {
			...cachedBusiness,
			...businessWithPreservedRole,
			users: cachedBusiness.users,
			businessUsers: cachedBusiness.businessUsers
		};
	});
}

export function mergeBusinessUserUpdate<T extends NotificationCacheUser>(
	user: T,
	response: BusinessUserUpdateCacheResponse,
	requestedRole?: string,
	guestRole = 'guest'
): T {
	const nextRole = response.businessRole ?? requestedRole;
	const crossesGuestBoundary =
		!!nextRole && user.businessRole !== nextRole && (user.businessRole === guestRole || nextRole === guestRole);

	return {
		...user,
		...response,
		...(nextRole ? { businessRole: nextRole } : {}),
		...(crossesGuestBoundary ? { notificationSettings: null } : {})
	};
}

export function mergeBusinessMembershipUpdate<T extends NotificationCacheBusinessUser>(
	businessUser: T,
	response: BusinessUserUpdateCacheResponse,
	requestedUpdate: Pick<BusinessUserUpdateCacheResponse, 'businessRole' | 'isApprover' | 'canPurchase' | 'adEditor'>,
	guestRole = 'guest'
): T {
	const nextRole = response.businessRole ?? requestedUpdate.businessRole ?? businessUser.role;
	const crossesGuestBoundary = businessUser.role !== nextRole && (businessUser.role === guestRole || nextRole === guestRole);

	return {
		...businessUser,
		...(nextRole ? { role: nextRole } : {}),
		...((response.isApprover ?? requestedUpdate.isApprover) !== undefined
			? { isApprover: response.isApprover ?? requestedUpdate.isApprover }
			: {}),
		...((response.canPurchase ?? requestedUpdate.canPurchase) !== undefined
			? { canPurchase: response.canPurchase ?? requestedUpdate.canPurchase }
			: {}),
		...((response.adEditor ?? requestedUpdate.adEditor) !== undefined
			? { adEditor: response.adEditor ?? requestedUpdate.adEditor }
			: {}),
		...(crossesGuestBoundary ? { notificationSettings: null } : {})
	};
}
